import Button from "@/common/components/Button";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { MultipleImageUploader } from "@/common/components/MultipleImageUploader";
import ResumeBox from "@/common/components/ResumeBox";
import TextArea from "@/common/components/TextArea";
import TextField from "@/common/components/TextField";
import { UnitTextField } from "@/common/components/UnitTextField";
import useBlobs from "@/common/hooks/queries/useBlobs";
import { formatCurrency, formatUnit } from "@/common/utils/formatters";
import { FormTransformers } from "@/common/utils/formTransformers";
import { unFormatCurrency, unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { BookAIcon, PlusIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useCreateDiagnosticObjectiveActivity } from "../../../../../../hooks/diagnosticObjectiveActivities/useCreateDiagnosticObjectiveActivity";
import { diagnosticObjectiveActivitySchema, DiagnosticObjectiveActivityValidationSchema } from "../../../../../../schemas/diagnosticObjectiveActivity.schema";
import SelectController from "@/common/components/SelectController";
import { diagnosticObjectiveActivityUnitForQuantityOptions } from "../../../../../../constants/diagnosticObjectiveActivityUnitForQuantity.constant";
import useDebounce from "@/common/hooks/useDebounce";
import { useMaintenanceEstablishmentTechnicalProfessionals } from "@/common/hooks/queries/useMaintenanceEstablishmentTechnicalProfessionals";
import { Mapper } from "@/common/services/mapper";
import { unities } from "@/common/constants/unities.constants";

interface CreateDiagnosticObjectiveActivityProps {
  diagnosticObjectiveId: string;
}

const CreateDiagnosticObjectiveActivity = ({
  diagnosticObjectiveId,
}: CreateDiagnosticObjectiveActivityProps) => {
  const { forwardingId } = useParams();

  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [formDisabled, setFormDisabled] = useState(false);

  const [searchTechnicalProfessional, setSearchTechnicalProfessional] = useDebounce('');

  const {
    data: technicalProfessionalsOptions,
  } = useMaintenanceEstablishmentTechnicalProfessionals({
    filters: {
      search: searchTechnicalProfessional,
    },
    mapper: Mapper.mapToOptions({
      valueFieldName: 'id',
      labelFieldName: 'display_name',
    }),
  });

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    setError,
    formState: {
      errors,
    },
  } = useForm<DiagnosticObjectiveActivityValidationSchema>({
    resolver: zodResolver(diagnosticObjectiveActivitySchema),
    disabled: formDisabled,
  });

  const {
    unit_for_quantity: watchUnitForQuantity,
    unit_price: watchUnitPrice,
    quantity: watchQuantity,
  } = useWatch({
    control,
  });

  const {
    mutate: createDiagnosticObjectiveActivity,
    isPending: createDiagnosticObjectiveActivityIsPending,
  } = useCreateDiagnosticObjectiveActivity({
    diagnosticObjectiveId,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding', forwardingId] });

        setIsOpen(false);

        setTimeout(reset, 500);
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setError,
          setMessages,
        })(error);
      }
    },
  });

  const onSubmit: SubmitHandler<DiagnosticObjectiveActivityValidationSchema> = (data) => createDiagnosticObjectiveActivity(data);

  const {
    mutateAsync: uploadBlob,
    isPending: uploadBlobIsPending,
  } = useBlobs();

  const totalValue = useMemo(() => {
    const parsedUnitPrice = watchUnitPrice ? unFormatCurrency(watchUnitPrice) : 0;
    const parsedQuantity = watchQuantity ? unFormatUnknownUnit(watchQuantity) : 0;

    return (parsedUnitPrice * parsedQuantity) || 0;
  }, [watchUnitForQuantity?.value, watchUnitPrice, watchQuantity]);

  useEffect(() => {
    if (!isOpen) {
      setTimeout(reset, 500);
    }
  }, [isOpen]);

  useEffect(() => {
    setFormDisabled(createDiagnosticObjectiveActivityIsPending);
  }, [createDiagnosticObjectiveActivityIsPending]);

  useEffect(() => {
    if (watchQuantity) {
      const parsedQuantity = unFormatUnknownUnit(watchQuantity);

      setValue(
        'quantity',
        formatUnit(parsedQuantity, watchUnitForQuantity?.value === 'hours' ? 'hours' : 'item')
      );
    }
  }, [watchUnitForQuantity?.value]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button operationKlassToCheckPermission="Establishment::Maintenance::DiagnosticObjectiveActivities::Create">
            <PlusIcon size={16} />
          </Button>
        )}
        title="Adicionar Serviço"
        titleClass="text-2xl"
        bodyClassName="text-white-800 overflow-y-auto max-h-[calc(100vh-200px)]"
        useCancellableFooter
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonText="Salvar"
        cancelButtonClassName="bg-white-200"
        confirmButtonClassName="bg-primary-blue"
        confirmButtonIsLoading={createDiagnosticObjectiveActivityIsPending}
        body={
          <div className="flex flex-col gap-4">
            <Controller
              control={control}
              name="display_name"
              render={({ field }) => (
                <TextField
                  label="Nome do Serviço de mão de obra"
                  markAsRequired
                  {...field}
                  errorMessage={errors.display_name?.message}
                />
              )}
            />

            <SelectController
              control={control}
              name="unit_for_quantity"
              options={diagnosticObjectiveActivityUnitForQuantityOptions}
              label="Medir quantidade do serviço em"
              markAsRequired
              isDisabled={formDisabled}
            />

            <div className="flex gap-3">
              <MultipleImageUploader
                label={(
                  <p className="text-xs font-bold text-center">
                    Anexe fotos do serviço
                  </p>
                )}
                name="images_attributes"
                control={control}
                fieldName="images_attachments_attributes"
                uploadBlob={uploadBlob}
                inputClassName="size-40 bg-white-200 border-white-200"
                imageCarouselClassName="size-40 bg-[#fff]"
                imageClassName="object-contain"
                disabled={uploadBlobIsPending || formDisabled}
                accept=".png, .jpg, .jpeg, .webp"
                uploadButtonLayout="floating"
              />

              <div className="flex-1 flex flex-col gap-3">
                <Controller
                  control={control}
                  name="quantity"
                  render={({ field }) => watchUnitForQuantity?.value === 'hours' ? (
                    <UnitTextField
                      label="Tempo de execução"
                      markAsRequired
                      decimals={0}
                      unit={unities['hours']}
                      maxValue={200}
                      forceValue
                      {...field}
                      errorMessage={errors.quantity?.message}
                    />
                  ) : (
                    <UnitTextField
                      label="Quantidade"
                      markAsRequired
                      decimals={0}
                      unit={unities['item']}
                      maxValue={200}
                      forceValue
                      {...field}
                      errorMessage={errors.quantity?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="unit_price"
                  render={({ field }) => (
                    <CurrencyTextField
                      label={watchUnitForQuantity?.value === 'hours' ? 'Preço por hora' : 'Preço unitário'}
                      markAsRequired
                      {...field}
                      errorMessage={errors.unit_price?.message}
                    />
                  )}
                />
              </div>
            </div>

            <SelectController
              control={control}
              name="technical_professional"
              label="Equipe técnica necessária"
              markAsRequired
              options={technicalProfessionalsOptions?.map((option) => ({
                ...option,
                icon: <BookAIcon strokeWidth={1} size={16} />
              }))}
              onInputChange={setSearchTechnicalProfessional}
              operationKlassToCheckPermission="Establishment::Maintenance::EstablishmentTechnicalProfessionals::Filter"
              optionLabelFormat="icon"
              isMulti
              errorMessage={errors.technical_professional?.message}
            />

            <Controller
              control={control}
              name="warranty_in_days"
              render={({ field }) => (
                <UnitTextField
                  label="Garantia"
                  markAsRequired
                  unit="Dias"
                  decimals={0}
                  {...field}
                  errorMessage={errors.warranty_in_days?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <TextArea
                  label="Descrição"
                  markAsRequired
                  rows={4}
                  {...field}
                  errorMessage={errors.description?.message}
                />
              )}
            />

            <ResumeBox
              label="Valor total"
              value={formatCurrency(totalValue)}
              padding="small"
              boxClassName="bg-white-200 rounded-md"
            />
          </div>
        }
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  );
}

export default CreateDiagnosticObjectiveActivity;
